import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import PasswordInput from '../components/PasswordInput';
import {
  redirectToMobileHandoff,
  capturePasswordResetMobileRedirect,
} from '../utils/mobileRedirect';
import { establishPasswordRecoverySession } from '../utils/passwordRecovery';

const Page = styled.div`
  background: #0a0a0a;
  background-image: linear-gradient(180deg, rgba(215, 196, 173, 0.2) 0%, rgba(10, 10, 10, 1) 65%);
  min-height: 100vh;
  padding: 2.5rem 1.5rem 3rem;
`;

const Wrapper = styled.div`
  max-width: 420px;
  margin: 2rem auto;
  background: var(--surface);
  color: var(--onSurface);
  padding: 1.75rem;
  border-radius: 16px;
  box-shadow: 0 22px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
`;

const Hero = styled.div`
  border-radius: 14px;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  color: var(--onSurface);
`;

const Logo = styled.img`
  width: 52px;
  height: 52px;
  object-fit: contain;
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HeroTitle = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  color: rgb(255, 255, 255);
`;

const HeroSubtitle = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.95rem;
`;

const BodyText = styled.p`
  margin: 0 0 1rem;
  color: #94a3b8;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const Field = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.25rem;
  color: var(--onSurface);
`;


const Button = styled.button`
  width: 100%;
  background: var(--primary);
  color: var(--onPrimary);
  padding: 0.75rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  font-weight: 600;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMsg = styled.p`
  color: var(--error);
  margin-top: 0.5rem;
`;

const StatusMsg = styled.p`
  margin-top: 0.5rem;
  color: #e2e8f0;
`;

const FooterLink = styled(Link)`
  color: #d7c4ad;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const FooterText = styled.p`
  margin-top: 1rem;
  color: #94a3b8;
  font-size: 0.95rem;
`;

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [handoffInProgress, setHandoffInProgress] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [handoffRefreshToken, setHandoffRefreshToken] = useState<string | null>(null);
  const mobileRedirect = useMemo(() => capturePasswordResetMobileRedirect(), []);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      capturePasswordResetMobileRedirect();
      const recovery = await establishPasswordRecoverySession();
      if (cancelled) return;
      if (recovery.error) {
        setError(recovery.error);
      }
      const { data } = await supabase.auth.getSession();
      if (!cancelled) {
        setHasSession(!!data?.session);
        setReady(true);
      }
    };
    init();
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setHasSession(!!session);
        setReady(true);
      }
    });
    return () => {
      cancelled = true;
      authListener.subscription.unsubscribe();
    };
  }, []);

  const returnToApp = async (refreshToken: string) => {
    if (!mobileRedirect) return false;
    setHandoffInProgress(true);
    setMessage('Password updated. Returning you to the app...');
    try {
      await redirectToMobileHandoff(mobileRedirect, refreshToken);
      return true;
    } catch (handoffError: any) {
      setHandoffInProgress(false);
      setError(
        handoffError?.message ||
          'Password updated, but we could not open the app automatically. Open New Church of Faith and sign in with your new password.'
      );
      return false;
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      const { data } = await supabase.auth.getSession();
      const refreshToken = data?.session?.refresh_token;
      if (mobileRedirect) {
        if (refreshToken) {
          setHandoffRefreshToken(refreshToken);
          const handedOff = await returnToApp(refreshToken);
          if (handedOff) return;
        }
        setMessage('Password updated. Return to the New Church of Faith app to continue.');
        return;
      }
      setMessage('Password updated. You can log in now.');
      setTimeout(() => navigate('/login', { replace: true }), 800);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Wrapper>
        <Hero>
          <Logo src="/ncofLogo.png" alt="New Church of Faith logo" />
          <HeroText>
            <HeroTitle>Reset password</HeroTitle>
            <HeroSubtitle>Choose a new password for your account</HeroSubtitle>
          </HeroText>
        </Hero>

        {!ready ? (
          <BodyText>Checking your reset link...</BodyText>
        ) : !hasSession ? (
          <>
            <BodyText>
              Your reset link may be invalid or expired. Open the link from your email, or request a
              new password reset from the login page.
            </BodyText>
            <FooterText>
              <FooterLink to="/login">Back to login</FooterLink>
            </FooterText>
          </>
        ) : (
          <form onSubmit={handleUpdate}>
            <Field>
              <Label htmlFor="password">New password</Label>
              <PasswordInput
                id="password"
                value={password}
                placeholder="At least 6 characters"
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Field>
            <Field>
              <Label htmlFor="confirm">Confirm password</Label>
              <PasswordInput
                id="confirm"
                value={confirm}
                placeholder="Re-enter password"
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
              />
            </Field>
            <Button type="submit" disabled={loading || handoffInProgress}>
              {handoffInProgress ? 'Opening app...' : loading ? 'Updating...' : 'Update password'}
            </Button>
            {error && <ErrorMsg>{error}</ErrorMsg>}
            {message && <StatusMsg>{message}</StatusMsg>}
            {mobileRedirect && (
              <>
                <FooterText>
                  After you update your password, this page will send you back to the
                  New Church of Faith app.
                </FooterText>
                {handoffRefreshToken ? (
                  <Button
                    type="button"
                    disabled={handoffInProgress}
                    onClick={() => void returnToApp(handoffRefreshToken)}
                  >
                    {handoffInProgress ? 'Opening app...' : 'Open New Church of Faith app'}
                  </Button>
                ) : null}
              </>
            )}
            <FooterText>
              <FooterLink to="/login">Back to login</FooterLink>
            </FooterText>
          </form>
        )}
      </Wrapper>
    </Page>
  );
};

export default ResetPasswordPage;
